import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AccountShell,
  BackLink,
  PageHeader,
  PaperRule,
  mono,
  serif,
} from "@/components/AccountLayout";
import { getMyAccount, type MyAccountResult } from "@/lib/account.functions";

export const Route = createFileRoute("/account_/purchase")({
  head: () => {
    const title = "Your purchase — WHO KNEW";
    const description =
      "Manage your WHO KNEW account, view your purchase, track your progress, or get in touch.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: "/account/purchase" },
      ],
    };
  },
  component: PurchasePage,
});

function formatMonthYear(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    month: "long",
    year: "numeric",
  }).format(new Date(iso));
}

function formatLongDate(d: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
}

function PurchasePage() {
  const navigate = useNavigate();
  const [state, setState] = useState<MyAccountResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMyAccount()
      .then((res) => {
        if (cancelled) return;
        if (!res.ok && res.error === "no_session") {
          navigate({ to: "/" });
          return;
        }
        setState(res);
      })
      .catch(() => {
        if (!cancelled) setState({ ok: false, error: "server" });
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const purchase = state?.ok ? state.purchase : null;

  const purchasedLabel = purchase ? `Purchased ${formatMonthYear(purchase.paidAt)}` : "";

  let refundLine: string | null = null;
  if (purchase) {
    if (purchase.isCorporate) {
      refundLine = "Corporate seat — no refund window applies.";
    } else {
      const deadline = new Date(
        new Date(purchase.paidAt).getTime() + 14 * 24 * 60 * 60 * 1000,
      );
      refundLine =
        deadline.getTime() < Date.now()
          ? "Your 14-day refund window has closed."
          : `Your 14-day refund window closes on ${formatLongDate(deadline)}.`;
    }
  }

  return (
    <AccountShell>
      <BackLink />
      <PageHeader eyebrow="WHAT YOU BOUGHT" title="Your purchase." />

      <div style={{ marginTop: "48px" }}>
        <h2
          className="text-ink font-bold"
          style={{
            ...serif,
            fontSize: "22px",
            lineHeight: 1.25,
            fontWeight: 700,
          }}
        >
          <span className="md:hidden">
            The Basic Business Accountancy Course for People Who Will Not, Under Any
            Circumstances, Take an Accountancy Course.
          </span>
          <span className="hidden md:inline" style={{ fontSize: "28px" }}>
            The Basic Business Accountancy Course for People Who Will Not, Under Any
            Circumstances, Take an Accountancy Course.
          </span>
        </h2>

        {state == null ? (
          <p
            className="italic"
            style={{ ...serif, fontSize: "16px", color: "#5A534A", marginTop: "12px" }}
          >
            Loading…
          </p>
        ) : purchase ? (
          <>
            <p style={{ ...serif, fontSize: "16px", color: "#5A534A", marginTop: "12px" }}>
              A <span className="text-burgundy">WHO KNEW</span> Course · {purchasedLabel}
            </p>
            <p
              className="font-bold uppercase tracking-[0.05em] text-burgundy"
              style={{ ...mono, fontSize: "11px", marginTop: "24px" }}
            >
              £99 · LIFETIME ACCESS · 14-DAY REFUND
            </p>
          </>
        ) : (
          <p
            className="italic"
            style={{ ...serif, fontSize: "16px", color: "#5A534A", marginTop: "12px" }}
          >
            No purchase on file yet.
          </p>
        )}
      </div>

      {purchase && (
        <>
          <PaperRule />

          <section style={{ paddingBottom: "96px" }}>
            <p
              className="font-bold uppercase tracking-[0.05em] text-burgundy"
              style={{ ...mono, fontSize: "11px" }}
            >
              REFUND WINDOW
            </p>
            <p
              className="text-ink"
              style={{ ...serif, fontSize: "18px", marginTop: "12px" }}
            >
              {refundLine}
            </p>
            {!purchase.isCorporate && (
              <p
                className="italic"
                style={{ ...serif, fontSize: "14px", color: "#5A534A", marginTop: "8px" }}
              >
                If it comes to it, just{" "}
                <Link
                  to="/account/contact"
                  search={{ topic: "refund" }}
                  className="text-burgundy underline decoration-burgundy/40 decoration-1 underline-offset-4 hover:decoration-2"
                >
                  send us a note
                </Link>
                .
              </p>
            )}
          </section>
        </>
      )}
    </AccountShell>
  );
}
